import React, { useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Button from '../../../components/ui/Button';

const SearchAndFilters = ({
  leagues = [],
  tournaments = [],
  selectedLeagueId = '',
  selectedTournamentId = '',
  selectedSeason = '',
  seasonOptions = [],
  onLeagueChange,
  onTournamentChange,
  onSeasonChange,
  onSearch,
  onFilterChange,
  isLoadingCompetitions = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    team: '',
    venue: '',
  });

  const leagueOptions = useMemo(() => {
    const list = selectedSeason
      ? leagues.filter((league) => !league.season || league.season === selectedSeason)
      : leagues;
    return [
      { value: '', label: 'Select League' },
      ...list.map((league) => ({
        value: String(league.id),
        label: `${league.name}${league.season ? ` · ${league.season}` : ''}`,
      })),
    ];
  }, [leagues, selectedSeason]);

  const tournamentOptions = useMemo(() => {
    const list = selectedSeason
      ? tournaments.filter((tournament) => !tournament.season || tournament.season === selectedSeason)
      : tournaments;
    return [
      { value: '', label: 'Select Tournament' },
      ...list.map((tournament) => ({
        value: String(tournament.id),
        label: `${tournament.name}${tournament.season ? ` · ${tournament.season}` : ''}`,
      })),
    ];
  }, [tournaments, selectedSeason]);

  const resolvedSeasonOptions = useMemo(() => {
    if (seasonOptions?.length) return seasonOptions;
    return [
      { value: '2024-25', label: '2024-25 Season' },
      { value: '2023-24', label: '2023-24 Season' },
      { value: '2022-23', label: '2022-23 Season' },
      { value: '2021-22', label: '2021-22 Season' },
    ];
  }, [seasonOptions]);

  const categoryOptions = [
    { value: '', label: 'All Categories' },
    { value: 'teams', label: 'Teams' },
    { value: 'players', label: 'Players' },
    { value: 'matches', label: 'Matches' },
    { value: 'leagues', label: 'Leagues' },
    { value: 'tournaments', label: 'Tournaments' },
    { value: 'news', label: 'News' },
    { value: 'statistics', label: 'Statistics' },
  ];

  const handleSearchChange = (e) => {
    const value = e?.target?.value;
    setSearchQuery(value);
    onSearch?.(value);
  };

  const handleFilterChange = (key, value) => {
    const next = { ...filters, [key]: value };
    setFilters(next);
    onFilterChange?.(next);
  };

  const clearFilters = () => {
    const cleared = { category: '', team: '', venue: '' };
    setFilters(cleared);
    setSearchQuery('');
    onSearch?.('');
    onFilterChange?.(cleared);
  };

  const hasActiveFilters = searchQuery || Object.values(filters).some((value) => value !== '');

  return (
    <div className="bg-card rounded-lg border border-border card-shadow mb-6">
      <div className="p-6">
        <div className="flex flex-col xl:flex-row gap-4 mb-4">
          <div className="flex-1">
            <div className="relative">
              <Icon
                name="Search"
                size={20}
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
              />
              <Input
                type="search"
                placeholder="Search teams, players, matches, or news..."
                value={searchQuery}
                onChange={handleSearchChange}
                className="pl-10"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <Select
              options={leagueOptions}
              value={selectedLeagueId ? String(selectedLeagueId) : ''}
              onChange={onLeagueChange}
              placeholder="Select League"
              searchable
              disabled={isLoadingCompetitions}
              className="w-full sm:w-48"
            />

            <Select
              options={tournamentOptions}
              value={selectedTournamentId ? String(selectedTournamentId) : ''}
              onChange={onTournamentChange}
              placeholder="Select Tournament"
              searchable
              disabled={isLoadingCompetitions}
              className="w-full sm:w-48"
            />

            <Select
              options={resolvedSeasonOptions}
              value={selectedSeason}
              onChange={onSeasonChange}
              placeholder="Select Season"
              className="w-full sm:w-40"
            />

            <Button
              variant="outline"
              onClick={() => setIsFiltersOpen(!isFiltersOpen)}
              iconName="Filter"
              iconPosition="left"
              className={isFiltersOpen ? 'bg-primary/10 text-primary border-primary' : ''}
            >
              Filters
            </Button>
          </div>
        </div>

        {isFiltersOpen && (
          <div className="border-t border-border pt-4 animate-slide-down">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <Select
                label="Category"
                options={categoryOptions}
                value={filters.category}
                onChange={(value) => handleFilterChange('category', value)}
                placeholder="All Categories"
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Icon name="Info" size={16} />
                <span>
                  Choose a league or tournament to load fixtures, groups/games, and results
                </span>
              </div>

              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  iconName="X"
                  iconPosition="left"
                >
                  Clear All
                </Button>
              )}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2 mt-4">
          <span className="text-sm text-muted-foreground">Quick filters:</span>
          {[
            { label: 'Live Matches', value: 'live-matches', icon: 'Radio' },
            { label: 'Top Teams', value: 'top-teams', icon: 'Trophy' },
            { label: 'Player Stats', value: 'player-stats', icon: 'BarChart3' },
            { label: 'Leagues', value: 'leagues', icon: 'Medal' },
            { label: 'Tournaments', value: 'tournaments', icon: 'Award' },
            { label: 'Recent News', value: 'recent-news', icon: 'Newspaper' },
          ].map((quickFilter) => (
            <Button
              key={quickFilter.value}
              variant="ghost"
              size="xs"
              onClick={() => handleFilterChange('category', quickFilter.value)}
              iconName={quickFilter.icon}
              iconPosition="left"
              className={`text-xs ${
                filters.category === quickFilter.value ? 'bg-primary/10 text-primary' : ''
              }`}
            >
              {quickFilter.label}
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SearchAndFilters;
